/**
 * useCustomLayout hook provides access to the custom layout context.
 *
 * @returns {CustomLayoutConfig} The custom layout configuration context.
 *
 * @throws Will throw an error if the hook is used outside of a `CustomLayout` component.
 */

import { useContext, useMemo } from "react";
import { CustomLayoutContext } from "./createCustomLayoutContext";
import { useTheme } from "styled-components";

export const useCustomLayout = () => {
  const context = useContext(CustomLayoutContext);
  if (!context) {
    throw new Error(
      "This component must be used within <UptimeCustomLayout> component."
    );
  }

  return context;
};

/**
 * useHyperlinkStyle hook provides the styles for hyperlinks based on the custom layout configuration.
 *
 * @returns {Object} An object containing the hyperlink style.
 * @returns {Object} hyperlinkStyle - The hyperlink style based on the custom layout configuration.
 *
 * @example
 * const { hyperlinkStyle } = useHyperlinkStyle();
 * <a style={hyperlinkStyle}>Link</a>
 */

export const useHyperlinkStyle = () => {
  const theme = useTheme();

  const customLayoutConfig = useCustomLayout();
  const { isUseHyperlinkStyle } = customLayoutConfig;

  const hyperlinkStyle = useMemo(
    () =>
      isUseHyperlinkStyle
        ? {
            color:
              theme.uptimeColors?.generalColors?.button?.hyperlink?.enabled + " !important",
            "&:hover": {
              textDecoration: "underline",
              color:
                theme.uptimeColors?.generalColors?.button?.hyperlink?.hover + " !important",
            },
          }
        : {},
    [theme, isUseHyperlinkStyle]
  );

  return { hyperlinkStyle };
};